/**
 * Master config for CRM field filter behaviour — operators, widgets, compiler flags, disabled keys.
 *
 * SOURCE: ENTITY_MODULE_EXPORT/config/crmFieldsConfig.ts
 * Adapted for Commission Manager: placed in src/shared/utils/ (not client-only)
 * because filterCompiler.ts (also in shared) depends on it for type-aware transforms.
 *
 * @dependencies src/shared/types/filters (FilterOperatorId)
 * @dependents   filterOperators, filterCompiler
 */

import type { FilterOperatorId } from "../types/filters";

// ── Field category ─────────────────────────────────────────────────────────────

export type FieldCat =
  | "boolean"
  | "number"
  | "text"
  | "date"
  | "datetime"
  | "enum"
  | "status"
  | "cat"
  | "currency"
  | "user"
  | "entity"
  | "crm_binding"
  | "file"
  | "other";

// ── Widget identifiers ────────────────────────────────────────────────────────

export type FilterWidgetId =
  | "TextInput"
  | "NumberInput"
  | "NumberRangeInput"
  | "BooleanSelect"
  | "MoneyInput"
  | "DictSelect"
  | "UserSelectorButton"
  | "CrmEntitySelectorButton"
  | "DatePeriodValueInput"
  | "NoValueWidget";

// ── Options source for dropdowns ──────────────────────────────────────────────

export type OptionsSource =
  | "field_meta"
  | "crm_status_list"
  | "crm_category_list"
  | "crm_currency_list";

// ── SDK selector type ─────────────────────────────────────────────────────────

export type SelectorType = "user_selector" | "crm_entity_selector";

// ── Compiler flags ────────────────────────────────────────────────────────────

export interface CompilerFlags {
  stripCurrency?: boolean;
  normalizeBoolStyle?: "YN" | "10";
  isMultipleAware?: boolean;
  crmBinding?: boolean;
}

// ── Per-type config ───────────────────────────────────────────────────────────

export interface FieldTypeFilterConfig {
  isFilterable: boolean;
  operators: FilterOperatorId[];
  widget: FilterWidgetId;
  optionsSource?: OptionsSource;
  selectorType?: SelectorType;
  compilerFlags?: CompilerFlags;
}

// ── Full config shape ─────────────────────────────────────────────────────────

export interface CrmFieldsConfig {
  typeConfig: Record<FieldCat, FieldTypeFilterConfig>;
  disabledFieldKeys: string[];
  fieldKeyOverrides: Record<string, Partial<FieldTypeFilterConfig>>;
}

// ── Shared operator lists ─────────────────────────────────────────────────────

const NUMERIC_OPERATORS: FilterOperatorId[] = [
  "equal_to",
  "not_equal_to",
  "more_than",
  "not_less_than",
  "less_than",
  "not_more_than",
  "is_in",
  "is_not_part_of",
  "in_range",
  "empty",
  "not_empty",
];

const DICT_OPERATORS: FilterOperatorId[] = [
  "equal_to",
  "not_equal_to",
  "is_in",
  "is_not_part_of",
  "empty",
  "not_empty",
];

const ENTITY_REF_OPERATORS: FilterOperatorId[] = [
  "equal_to",
  "not_equal_to",
  "is_in",
  "is_not_part_of",
  "empty",
  "not_empty",
];

const DATE_OPERATORS: FilterOperatorId[] = [
  "NONE",
  "CURRENT_DAY",
  "YESTERDAY",
  "TOMORROW",
  "CURRENT_WEEK",
  "CURRENT_MONTH",
  "CURRENT_QUARTER",
  "LAST_7_DAYS",
  "LAST_30_DAYS",
  "LAST_60_DAYS",
  "LAST_90_DAYS",
  "LAST_WEEK",
  "LAST_MONTH",
  "NEXT_WEEK",
  "NEXT_MONTH",
  "PREV_DAYS",
  "NEXT_DAYS",
  "EXACT",
  "MONTH",
  "QUARTER",
  "YEAR",
  "RANGE",
  "empty",
  "not_empty",
];

// ── Master config ─────────────────────────────────────────────────────────────

export const CRM_FIELDS_CONFIG: CrmFieldsConfig = {
  typeConfig: {
    text: {
      isFilterable: true,
      operators: [
        "equal_to",
        "not_equal_to",
        "includes",
        "does_not_include",
        "is_in",
        "is_not_part_of",
        "empty",
        "not_empty",
      ],
      widget: "TextInput",
    },

    number: {
      isFilterable: true,
      operators: NUMERIC_OPERATORS,
      widget: "NumberInput",
      compilerFlags: { isMultipleAware: true },
    },

    boolean: {
      isFilterable: true,
      operators: ["equal_to", "empty", "not_empty"],
      widget: "BooleanSelect",
      compilerFlags: { normalizeBoolStyle: "YN" },
    },

    date: {
      isFilterable: true,
      operators: DATE_OPERATORS,
      widget: "DatePeriodValueInput",
    },

    datetime: {
      isFilterable: true,
      operators: DATE_OPERATORS,
      widget: "DatePeriodValueInput",
    },

    enum: {
      isFilterable: true,
      operators: DICT_OPERATORS,
      widget: "DictSelect",
      optionsSource: "field_meta",
      compilerFlags: { isMultipleAware: true },
    },

    status: {
      isFilterable: true,
      operators: DICT_OPERATORS,
      widget: "DictSelect",
      optionsSource: "crm_status_list",
      compilerFlags: { isMultipleAware: true },
    },

    cat: {
      isFilterable: true,
      operators: DICT_OPERATORS,
      widget: "DictSelect",
      optionsSource: "crm_category_list",
    },

    currency: {
      isFilterable: true,
      operators: DICT_OPERATORS,
      widget: "DictSelect",
      optionsSource: "crm_currency_list",
    },

    user: {
      isFilterable: true,
      operators: ENTITY_REF_OPERATORS,
      widget: "UserSelectorButton",
      selectorType: "user_selector",
      compilerFlags: { isMultipleAware: true },
    },

    entity: {
      isFilterable: true,
      operators: ENTITY_REF_OPERATORS,
      widget: "CrmEntitySelectorButton",
      selectorType: "crm_entity_selector",
      compilerFlags: { isMultipleAware: true },
    },

    crm_binding: {
      isFilterable: true,
      operators: ENTITY_REF_OPERATORS,
      widget: "CrmEntitySelectorButton",
      selectorType: "crm_entity_selector",
      compilerFlags: { crmBinding: true, isMultipleAware: true },
    },

    file: {
      isFilterable: true,
      operators: ["empty", "not_empty"],
      widget: "NoValueWidget",
    },

    other: {
      isFilterable: true,
      operators: ["empty", "not_empty"],
      widget: "NoValueWidget",
    },
  },

  disabledFieldKeys: [
    "fm",
    "photo",
    "logo",
    "id",
    "xmlId",
    "webformId",
    "originId",
    "originatorId",
    "searchContent",
  ],

  fieldKeyOverrides: {},
};

// ── Helper functions ──────────────────────────────────────────────────────────

export function getFieldTypeConfig(cat: FieldCat): FieldTypeFilterConfig {
  return CRM_FIELDS_CONFIG.typeConfig[cat] ?? CRM_FIELDS_CONFIG.typeConfig.other;
}

export function isFieldKeyDisabled(key: string): boolean {
  return CRM_FIELDS_CONFIG.disabledFieldKeys.includes(key);
}

export function getEffectiveFieldConfig(cat: FieldCat, key: string): FieldTypeFilterConfig {
  const base = getFieldTypeConfig(cat);
  const override = CRM_FIELDS_CONFIG.fieldKeyOverrides[key];
  if (!override) return base;
  return { ...base, ...override };
}

export function isFieldFilterable(cat: FieldCat): boolean {
  return getFieldTypeConfig(cat).isFilterable;
}

/**
 * Bitrix field types that map to each FieldCat.
 */
export const FIELD_TYPE_TO_CAT: Record<string, FieldCat> = {
  boolean: "boolean",
  integer: "number",
  double: "number",
  money: "number",
  string: "text",
  text: "text",
  url: "text",
  link: "text",
  date: "date",
  datetime: "datetime",
  enumeration: "enum",
  crm_status: "status",
  crm_category: "cat",
  crm_currency: "currency",
  user: "user",
  employee: "user",
  crm_company: "entity",
  crm_contact: "entity",
  crm_deal: "entity",
  crm_lead: "entity",
  crm_quote: "entity",
  crm_entity: "entity",
  crm: "crm_binding",
  file: "file",
  phone: "text",
  email: "text",
  address: "other",
  crm_multifield: "other",
  resourcebooking: "other",
  iblock_section: "other",
  iblock_element: "other",
  location: "other",
  crm_enum_activitytype: "enum",
  crm_enum_activitystatus: "enum",
  crm_enum_activitypriority: "enum",
  crm_enum_activitydirection: "enum",
  crm_enum_contenttype: "enum",
  crm_enum_activitynotifytype: "enum",
};
